/**
 * Folder Structure Utilities
 *
 * Generates human-readable folder paths for Google Drive storage.
 * Handles name sanitization and path building for all entity types.
 */

import type { EntityType, FileCategory, UploadContext } from '../../types/storage';
import type { DocumentEntityType } from '../documentTypeRegistry';
import { getDocTypeLabels } from '../documentTypeRegistry';
import { getDocumentYear } from '../../utils/documentYear';

/**
 * Maximum length for folder/file names in Google Drive
 */
const MAX_NAME_LENGTH = 100;

/**
 * Characters not allowed in Google Drive folder names
 */
const INVALID_CHARS_REGEX = /[<>:"/\\|?*\x00-\x1f]/g;

/**
 * Sanitize a name for use as a folder or file name
 * - Removes invalid characters
 * - Trims whitespace
 * - Truncates to max length
 * - Preserves spaces (Google Drive supports spaces and they're more readable for users)
 */
export function sanitizeName(name: string): string {
  if (!name || typeof name !== 'string') {
    return 'Unnamed';
  }

  let sanitized = name
    // Remove invalid characters (but keep spaces)
    .replace(INVALID_CHARS_REGEX, '')
    // Replace forward slashes with dashes
    .replace(/\//g, '-')
    // Trim whitespace
    .trim()
    // Normalize multiple spaces to single space
    .replace(/\s+/g, ' ');

  // Truncate if too long
  if (sanitized.length > MAX_NAME_LENGTH) {
    sanitized = sanitized.substring(0, MAX_NAME_LENGTH).trim();
  }

  // Fallback if empty after sanitization
  return sanitized || 'Unnamed';
}

/**
 * Map entity type to human-readable folder name
 */
export function getEntityTypeFolderName(entityType: EntityType): string {
  const folderNames: Record<EntityType, string> = {
    address: 'Addresses',
    personal: 'Personal',
    vehicle: 'Vehicles',
    insurance: 'Insurance',
    healthcare: 'Healthcare',
    financial: 'Financial',
    'service-provider': 'Service_Providers',
    utility: 'Utilities',
    profile: 'Profile',
  };

  return folderNames[entityType] || 'Other';
}

/**
 * Map file category to human-readable folder name
 */
export function getFileCategoryFolderName(category: FileCategory): string {
  const folderNames: Record<FileCategory, string> = {
    photos: 'Photos',
    documents: 'Documents',
    'id-cards': 'ID_Cards',
    avatar: 'Avatar',
  };

  return folderNames[category] || 'Files';
}

/**
 * Build the full folder path for a file upload
 *
 * Structure: LucidDock/{ProfileName}/{EntityType}/{EntityName}/{FileCategory}
 *
 * Child entities (utility, service-provider) use a flat folder under their parent:
 *   LucidDock/{Profile}/Addresses/{AddressName}/Utilities/
 *   (entity name + year are baked into the filename)
 *
 * Examples:
 * - LucidDock/John Doe/Addresses/123 Main St/Photos
 * - LucidDock/John Doe/Addresses/123 Main St/Utilities/Chariot Energy — Bill — 2026.pdf
 * - LucidDock/John Doe/Addresses/123 Main St/Service_Providers/Joe Plumber — Invoice — 2025.pdf
 * - LucidDock/John Doe/Vehicles/2023 Toyota Camry/Documents
 */
export function buildFolderPath(context: UploadContext): string {
  const { profileName, entityType, entityName, fileCategory, year } = context;

  const sanitizedProfileName = sanitizeName(profileName);
  const entityTypeFolderName = getEntityTypeFolderName(entityType);
  const sanitizedEntityName = sanitizeName(entityName);
  const categoryFolderName = getFileCategoryFolderName(fileCategory);

  // Special case for profile avatar - no entity subfolder needed
  if (entityType === 'profile' && fileCategory === 'avatar') {
    return `LucidDock/${sanitizedProfileName}/Profile/Avatar`;
  }

  // Child entities nest under parent folder (e.g., utility under its address)
  let basePath: string;
  if (context.parentEntityType && context.parentEntityName) {
    const parentTypeFolderName = getEntityTypeFolderName(context.parentEntityType);
    const sanitizedParentName = sanitizeName(context.parentEntityName);

    // Utilities and service-providers use a flat folder structure:
    //   Addresses/{Address}/Utilities/{file}
    // Entity name + year are baked into the filename instead of subfolders.
    if (entityType === 'utility' || entityType === 'service-provider') {
      return `LucidDock/${sanitizedProfileName}/${parentTypeFolderName}/${sanitizedParentName}/${entityTypeFolderName}`;
    }

    basePath = `LucidDock/${sanitizedProfileName}/${parentTypeFolderName}/${sanitizedParentName}/${entityTypeFolderName}/${sanitizedEntityName}/${categoryFolderName}`;
  } else {
    basePath = `LucidDock/${sanitizedProfileName}/${entityTypeFolderName}/${sanitizedEntityName}/${categoryFolderName}`;
  }

  // Add year subfolder for dated documents (e.g., Documents/2025/)
  if (year) {
    return `${basePath}/${year}`;
  }

  return basePath;
}

/**
 * Build the full file path including the file name
 *
 * Examples:
 * - LucidDock/John_Doe/Addresses/123_Main_St/Photos/front_yard.jpg
 * - LucidDock/John_Doe/Vehicles/2023_Toyota_Camry/Documents/registration.pdf
 */
export function buildFilePath(context: UploadContext): string {
  const folderPath = buildFolderPath(context);
  const sanitizedFileName = sanitizeName(context.fileName);

  return `${folderPath}/${sanitizedFileName}`;
}

/**
 * Split a folder path into its component parts
 * Useful for iterative folder creation
 */
export function splitFolderPath(path: string): string[] {
  return path.split('/').filter(Boolean);
}

/**
 * Get all parent paths for a given path
 * Used for creating nested folders
 *
 * Example: "LucidDock/John/Addresses/Home/Photos"
 * Returns: [
 *   "LucidDock",
 *   "LucidDock/John",
 *   "LucidDock/John/Addresses",
 *   "LucidDock/John/Addresses/Home",
 *   "LucidDock/John/Addresses/Home/Photos"
 * ]
 */
export function getAllParentPaths(path: string): string[] {
  const parts = splitFolderPath(path);
  const paths: string[] = [];

  let currentPath = '';
  for (const part of parts) {
    currentPath = currentPath ? `${currentPath}/${part}` : part;
    paths.push(currentPath);
  }

  return paths;
}

/**
 * Build a structured filename for child entity files (utilities, service providers).
 *
 * Pattern: "{EntityName} — {TypeLabel} — {Year}.ext"
 *   e.g.  "Chariot Energy — Bill — 2026.pdf"
 *
 * Falls back gracefully when type or year is missing:
 *   - No type  → "Chariot Energy — 2026.pdf"
 *   - No year  → "Chariot Energy — Bill.pdf"
 *   - Neither  → "Chariot Energy — photo.jpg" (uses original filename)
 *
 * @param entityName   Provider/utility name (from form)
 * @param entityType   'service-provider' | 'utility' — for doc type label lookup
 * @param originalFile Original file name with extension (e.g. "scan.pdf")
 * @param docType      Doc type key from picker (e.g. 'bill', 'invoice')
 * @param doc          Document/photo object with date fields for year derivation
 */
export function buildChildEntityFileName(
  entityName: string,
  entityType: DocumentEntityType,
  originalFile: string,
  docType?: string,
  doc?: { effectiveDate?: string; expiresAt?: string; uploadedAt?: string },
): string {
  const ext = originalFile.includes('.') ? originalFile.substring(originalFile.lastIndexOf('.')) : '';
  const labels = getDocTypeLabels(entityType);
  const typeLabel = docType ? labels[docType] : undefined;
  const year = doc ? getDocumentYear(doc) : null;

  const parts = [sanitizeName(entityName)];
  if (typeLabel) parts.push(typeLabel);
  if (year) parts.push(String(year));

  // If we have neither type nor year, append original filename (minus extension)
  if (!typeLabel && !year) {
    const baseName = originalFile.includes('.')
      ? originalFile.substring(0, originalFile.lastIndexOf('.'))
      : originalFile;
    parts.push(sanitizeName(baseName));
  }

  return `${parts.join(' — ')}${ext}`;
}

/**
 * Generate a unique file name to avoid conflicts
 * Appends timestamp if needed
 */
export function generateUniqueFileName(originalName: string): string {
  const lastDotIndex = originalName.lastIndexOf('.');

  if (lastDotIndex === -1) {
    return sanitizeName(originalName);
  }

  const name = originalName.substring(0, lastDotIndex);
  const extension = originalName.substring(lastDotIndex);

  return `${sanitizeName(name)}${extension}`;
}

/**
 * Build Firebase Storage path (for comparison/migration purposes)
 * This matches the existing Firebase storage path structure
 */
export function buildFirebaseStoragePath(context: UploadContext): string {
  const { profileId, entityType, entityId, fileCategory, fileName } = context;

  // Firebase uses IDs instead of human-readable names
  return `profiles/${profileId}/${entityType}/${entityId}/${fileCategory}/${fileName}`;
}

/**
 * Extract entity info from a Google Drive folder path
 * Useful for reverse lookups
 */
export function parseGoogleDrivePath(
  path: string
): { profileName: string; entityType: string; entityName: string; fileCategory: string; year?: string } | null {
  const parts = splitFolderPath(path);

  // Expected: LucidDock/{profileName}/{entityType}/{entityName}/{fileCategory}[/{year}]
  if (parts.length < 5 || parts[0] !== 'LucidDock') {
    return null;
  }

  return {
    profileName: parts[1],
    entityType: parts[2],
    entityName: parts[3],
    fileCategory: parts[4],
    year: parts[5], // Optional year segment
  };
}
